import { useMemo } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import type { ColDef, ICellRendererParams } from "ag-grid-community";
import { useTRPC } from "../../../../trpc.js";
import { useQuery } from "@tanstack/react-query";
import { Loader } from "../../../../components/Loader.js";
import type { ProductId } from "@project/common";
import { Badge } from "@mantine/core";
import { AgGridTable } from "../../../../components/AgGridTable.js";

interface Row {
  addOn: string;
  isActive: boolean;
  price: string;
  spend: string;
}

function IsActiveBadge({ value }: ICellRendererParams<Row, boolean>) {
  if (value) {
    return (
      <Badge
        variant="outline"
        color="success"
      >
        <FormattedMessage
          defaultMessage="Active"
          id="label.active"
        />
      </Badge>
    );
  }
  else {
    return (
      <Badge
        variant="outline"
        color="error"
      >
        <FormattedMessage
          defaultMessage="Inactive"
          id="label.inactive"
        />
      </Badge>
    );
  }
}

export function AddonsTable() {
  const trpc = useTRPC();
  const intl = useIntl();

  const {
    data: currentSubscription,
    isLoading: isLoadingSubscriptions,
  } = useQuery({
    ...trpc.getCurrentSubscriptions.queryOptions(),
    select: subscriptions =>
      subscriptions.find(s => s.status === "active"),
  });

  const cols = useMemo((): ColDef<Row>[] => [
    {
      field: "addOn",
      headerName: intl.formatMessage({ defaultMessage: "Add-on", id: "label.addOn" }),
    },
    {
      field: "isActive",
      headerName: intl.formatMessage({ defaultMessage: "State", id: "label.state" }),
      cellRenderer: IsActiveBadge,
    },
    {
      field: "price",
      headerName: intl.formatMessage({ defaultMessage: "Price", id: "label.price" }),
    },
    {
      field: "spend",
      headerName: intl.formatMessage({ defaultMessage: "Spend", id: "label.spend" }),
    },
  ], [intl]);

  const getPlan = (nickname: ProductId) =>
    currentSubscription?.prices.find(price => price.nickname == nickname);

  const recurrentBilling = (nickname: ProductId) =>
    intl.formatMessage(
      {
        id: "label.pricePerInterval",
        defaultMessage: "{amount}/{interval, select, month {month} year {year} other {{interval}}}",
      },
      {
        amount: intl.formatNumber(
          Number(getPlan(nickname)?.unitAmount),
          {
            style: "currency",
            currency: getPlan(nickname)?.currency,
          },
        ),
        interval: getPlan(nickname)?.recurring?.interval,
      },
    );

  const rows = useMemo((): Partial<Row>[] | null =>
    currentSubscription ?
        [
          {
            addOn: intl.formatMessage({ defaultMessage: "No Mapka attribution", id: "label.noAttribution" }),
            isActive: Boolean(getPlan("mapka_no_attribution")),
            price: recurrentBilling("mapka_no_attribution"),
            spend: intl.formatNumber(800, {
              style: "currency",
              currency: getPlan("mapka_no_attribution")?.currency,
            }),
            // where do we store how much a user actually spends on an add-on? I looked through
            // all the tables of ./packages/api/src/db/domain.ts and didn't find any table for that.
          },
        ] :
      null, [intl]);

  if (isLoadingSubscriptions) {
    return <Loader />;
  }

  if (!currentSubscription) {
    return null;
  }

  return (
    <AgGridTable
      rowData={rows}
      columnDefs={cols}
    />
  );
};
